import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { flagInSet, isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { getAssetCdnPath } from '../../../webmodule-common/other/common/assets';
import { glossary } from '../../../localization/global';
import { html, TemplateResult } from 'lit';
import { LangQuoteHelperFunctions } from '../../../localization/scripts/quotes/quote-helper-functions-lang';
import {
  Quote,
  QuoteItem,
  QuoteItemType,
  QuoteState,
  QuoteType,
  ViewQuoteSummary
} from '../../api/dealer-api-interface-quote';
import { quoteItemContentType } from './default-quote-item-content-type';
import { supplierQuoteItemContentType } from './supplier-quote-item-content-type';

export const processQuoteItemState = {
  updating: 'updating',
  validating: 'validating',
  completed: 'completed',
  finalizing: 'finalizing',
  errors: 'errors'
};

export const getQuoteNumberSuffix = (q: ViewQuoteSummary | Quote | null, includeState = true) => {
  if (!q) return '';
  const suffix = isEmptyOrSpace(q.quoteSuffix) ? '    ' : `-${q.quoteSuffix.padStart(3, ' ')}`;

  return q.quoteNumber === 0
    ? includeState
      ? LangQuoteHelperFunctions.draft()
      : ''
    : `${q.quoteNumber.toString().padStart(6, '0')}${suffix}`;
};

export const formatQuoteNumber = (
  quoteSuffix: string,
  quoteNumber: number,
  quoteType: QuoteType,
  includeState = true
) => {
  const suffix = isEmptyOrSpace(quoteSuffix) ? '    ' : `-${quoteSuffix.padStart(3, ' ')}`;

  const prefix =
    quoteType === QuoteType.Estimate
      ? LangQuoteHelperFunctions.estimateAbbrev()
      : quoteType === QuoteType.Quote
        ? LangQuoteHelperFunctions.quoteAbbrev()
        : quoteType === QuoteType.Display
          ? LangQuoteHelperFunctions.displaySampleAbbrev()
          : LangQuoteHelperFunctions.warrantyItemAbbrev();
  return quoteNumber === 0
    ? includeState
      ? LangQuoteHelperFunctions.draft()
      : ''
    : `${prefix}: ${quoteNumber.toString().padStart(6, '0')}${suffix}`;
};

export const getQuoteNumberFormatted = (q: ViewQuoteSummary | Quote | null | undefined, includeState = true) => {
  if (!q) return '';

  return formatQuoteNumber(q.quoteSuffix, q.quoteNumber, q.quoteType, includeState);
};

export const internalExtendedListPriceField = 'extendedListPrice';
export const internalMultiplierField = 'multiplier';
export const internalPriceAdjustmentSupplierField = 'priceAdjustmentSupplier';
export const internalDealerCostField = 'dealerCost';
export const internalMarginField = 'margin';
export const internalPriceAdjustmentField = 'priceAdjustment';

export const internalQuoteItemFields = [
  internalExtendedListPriceField,
  internalMultiplierField,
  internalPriceAdjustmentSupplierField,
  internalDealerCostField,
  internalMarginField,
  internalPriceAdjustmentField
];

export function quoteTypeDisplayString(value: QuoteType): string {
  switch (value) {
    case QuoteType.None:
      return '';
    case QuoteType.Estimate:
      return LangQuoteHelperFunctions.estimate();
    case QuoteType.Display:
      return LangQuoteHelperFunctions.displaySample();
    case QuoteType.Quote:
      return LangQuoteHelperFunctions.quote();
    case QuoteType.Warranty:
      return LangQuoteHelperFunctions.warrantyItem();
    default:
      throw new DevelopmentError(`QuoteTypeToString: Unknown QuoteType value: ${value}`);
  }
}

export function getQuoteItemTab(title: string, itemPosition?: number): TemplateResult {
  if (itemPosition === undefined)
    return html` <div class="tab-caption-block"><span class="tab-caption">${title}</span></div>`;
  return html` <div class="tab-caption-block">
    <span class="tab-pos">#${itemPosition}</span><span class="tab-caption">${title}</span>
  </div>`;
}

export const getQuoteTitle = (q: ViewQuoteSummary | Quote | null | undefined, state?: QuoteState) => {
  if (!q) return html``;
  const quote = q as Quote;
  let quoteNumber = getQuoteNumberFormatted(quote, false);
  if (!isEmptyOrSpace(quoteNumber)) quoteNumber += ' ';
  return html`${quoteNumber}${quote.title} ${getQuoteStatus(state ?? quote.state, true)}`;
};

export const getQuoteTitleStr = (
  q: ViewQuoteSummary | Quote | null | undefined,
  state?: QuoteState,
  excludeState?: boolean
): string => {
  if (!q) return ``;
  const quote = q as Quote;
  let quoteNumber = getQuoteNumberFormatted(quote, false);
  const stateTempl = excludeState ? '' : getQuoteStatusStr(state ?? quote.state);
  if (!isEmptyOrSpace(quoteNumber)) quoteNumber += ' ';
  return `${quoteNumber}${quote.title} ${stateTempl}`;
};
export const getQuoteStatusStr = (state: number) => {
  const value = QuoteState[state].toLowerCase();

  return glossary(`quote-state-${value}`);
};

export const getQuoteStatus = (state: number, asBadge = false, asRaw = false) => {
  const statesToRender = buildStatusSequence(state);

  if (!asBadge) {
    const statusText = statesToRender.map(s => getQuoteStatusStr(s)).join(' ');
    return html`${statusText}`;
  }

  if (asRaw) {
    return statesToRender.map(s => toBadgeMarkup(s, true)).join('');
  }

  return html`${statesToRender.map(s => toBadgeMarkup(s, false))}`;
};

function buildStatusSequence(state: number): number[] {
  const hasCopying = flagInSet(state, QuoteState.Copying);
  const baseState = hasCopying ? state - QuoteState.Copying : state;
  const states: number[] = [];

  if (baseState !== 0 || !hasCopying) {
    states.push(baseState);
  }

  if (hasCopying) return [QuoteState.Copying];

  return states;
}

function toBadgeMarkup(state: number, asRaw: boolean) {
  const value = QuoteState[state]?.toLowerCase() ?? 'unknown';
  const text = glossary(`quote-state-${value}`) ?? value;

  return asRaw
    ? `<span class="badge badge-${value}">${text}</span>`
    : html`<span class="badge badge-${value}">${text}</span>`;
}

export function htmlEncode(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function quoteItemThumbSrc(item: QuoteItem): string {
  let src = '';
  if (isShipping(item)) {
    src = getAssetCdnPath('images/delivery.svg');
  } else if (isFreehand(item)) {
    src = getAssetCdnPath('images/freehand.svg');
  } else if (isSpecialItem(item)) {
    src = getAssetCdnPath('images/special-item.svg');
  } else if (isSSI(item)) {
    src = getAssetCdnPath('images/freehand-ssi.svg');
  } else if (isDealerQuotePriceAdjustment(item)) {
    src = getAssetCdnPath('images/dealer-quote-price-adjustment.svg');
  } else src = getAssetCdnPath('images/freehand.svg');
  return src;
}

export function isShipping(quoteItem: QuoteItem) {
  return (
    (quoteItem.itemType === QuoteItemType.Basic && quoteItem.quoteItemContentType === quoteItemContentType.shipping) ||
    isSSI(quoteItem, quoteItemContentType.Reserved_ServerSideShippingItem)
  );
}

export function isDefaultShipping(quoteItem: QuoteItem) {
  return quoteItem.itemType === QuoteItemType.Basic && quoteItem.quoteItemContentType === quoteItemContentType.shipping;
}

export function isFreehand(quoteItem: QuoteItem) {
  return quoteItem.itemType === QuoteItemType.Basic && quoteItem.quoteItemContentType === quoteItemContentType.freehand;
}

export function isAnyFreehand(quoteItem: QuoteItem) {
  return isFreehand(quoteItem) || isDealerQuotePriceAdjustment(quoteItem);
}

export function isDealerQuotePriceAdjustment(quoteItem: QuoteItem) {
  return (
    quoteItem.itemType === QuoteItemType.Basic &&
    quoteItem.quoteItemContentType === quoteItemContentType.dealerQuotePriceAdjustment
  );
}

export function isSpecialItem(quoteItem: QuoteItem) {
  return quoteItem.quoteItemContentType === quoteItemContentType.specialItem;
}

export function isSupplierFrameItem(quoteItem: QuoteItem) {
  return isFrame(quoteItem) || isSupplierSpecialItem(quoteItem);
}

export function isSupplierSpecialItem(quoteItem?: QuoteItem) {
  if (!quoteItem) return false;
  return (
    quoteItem.itemType === QuoteItemType.Provider && quoteItem.quoteItemContentType === quoteItemContentType.specialItem
  );
}

export const serverSideContentTypeOffset = quoteItemContentType.ServerSideContentTypeOffset;
export const serverSideShippingItem = quoteItemContentType.Reserved_ServerSideShippingItem;
export const serverSideReservedMax = quoteItemContentType.ServerSideReservedMax;
export const serverSideUserReservedMax = quoteItemContentType.ServerSideUserReservedMax;

export function isContentTypeSSI(aType: number) {
  return aType >= serverSideContentTypeOffset && aType <= serverSideUserReservedMax;
}

export function isSSI(quoteItem?: QuoteItem | null, ofSpecificType?: number) {
  if (!quoteItem) return false;
  const valid =
    quoteItem.itemType === QuoteItemType.Basic &&
    quoteItem.quoteItemContentType >= serverSideContentTypeOffset &&
    quoteItem.quoteItemContentType <= serverSideUserReservedMax;
  return ofSpecificType ? valid && quoteItem.quoteItemContentType === ofSpecificType : valid;
}

export function isFrame(quoteItem: QuoteItem) {
  return (
    quoteItem.itemType === QuoteItemType.Provider &&
    quoteItem.quoteItemContentType === supplierQuoteItemContentType.CID_FRAM
  );
}

export function isSupplierItem(quoteItem: QuoteItem) {
  return isFrame(quoteItem) || isSpecialItem(quoteItem);
}

export function mustAppearOnPurchaseOrder(quoteItem: QuoteItem) {
  return !isAnyFreehand(quoteItem) && !isShipping(quoteItem);
}

export function canAppearOnSupplierOrder(quoteItem: QuoteItem) {
  //return isSupplierItem(quoteItem) || isSSI(quoteItem) || isShipping(quoteItem);
  return !isAnyFreehand(quoteItem);
}

export function isQuoteDraft(state: QuoteState | number): boolean {
  return flagInSet(state, QuoteState.Draft);
}

export function isQuoteActive(state: QuoteState | number): boolean {
  return flagInSet(state, QuoteState.Active);
}

export function isQuoteCopying(state: QuoteState | number): boolean {
  return flagInSet(state, QuoteState.Copying);
}
